import React from 'react';
import ReactDOM from 'react-dom/client';
import App from './App';
import './index.css';
import '@fontsource/inter/300.css';
import '@fontsource/inter/400.css';
import '@fontsource/inter/500.css';
import '@fontsource/inter/600.css';
import '@fontsource/inter/700.css';

interface ErrorBoundaryProps {
  children?: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  public state: ErrorBoundaryState = { hasError: false, error: null };

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error("Critical Error Caught:", error, errorInfo);
  }

  handleEmergencyExport = () => {
    try {
      // Collect all data from localStorage and create a backup file.
      const backupData: Record<string, any> = {};
      for (let i = 0; i < localStorage.length; i++) {
        const key = localStorage.key(i);
        if (key && key.startsWith('noxicon_')) {
          backupData[key] = localStorage.getItem(key);
        }
      }

      const blob = new Blob([JSON.stringify(backupData, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `noxicon_emergency_backup_${new Date().toISOString().slice(0, 19).replace(/:/g, '-')}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      alert('Emergency backup file has been downloaded. You can restore this file using the [Import] feature in the Admin page.');
    } catch (e) {
      alert('An error occurred while extracting data.');
    }
  };

  handleReset = () => {
    if (window.confirm('Are you sure you want to reset? Data will be permanently deleted if not backed up.')) {
      localStorage.clear();
      window.location.reload();
    }
  };

  render() {
    if (this.state.hasError) {
      return (
        <div style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: '#0F172A',
          fontFamily: "'Inter', sans-serif",
          padding: '20px',
          textAlign: 'center'
        }}>
          <h1 style={{ fontSize: '24px', fontWeight: '900', color: '#E2E8F0', marginBottom: '16px' }}>
            A temporary error occurred.
          </h1>
          <p style={{ color: '#94A3B8', marginBottom: '32px', maxWidth: '440px', lineHeight: '1.6', fontSize: '14px' }}>
            Could not display the screen due to a data structure conflict.<br />
            For safety, please <strong>save data to your PC (Backup)</strong> first, then reset the app.
          </p>
          <div style={{ marginBottom: '24px', padding: '15px', background: '#DC262620', borderRadius: '12px', color: '#FCA5A5', fontSize: '12px', maxWidth: '500px', overflow: 'auto', textAlign: 'left' }}>
            <strong>Debug Info:</strong> {this.state.error?.message || 'Unknown Error'}
          </div>

          <div style={{ display: 'flex', gap: '12px', flexDirection: 'column', width: '100%', maxWidth: '300px' }}>
            <button
              onClick={this.handleEmergencyExport}
              style={{
                backgroundColor: '#38BDF8',
                color: '#0F172A',
                border: 'none',
                padding: '16px',
                borderRadius: '16px',
                fontSize: '15px',
                fontWeight: 'bold',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
              }}
            >
              <svg width="20" height="20" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" /></svg>
              Emergency Data Rescue (Download Backup)
            </button>

            <button
              onClick={this.handleReset}
              style={{
                backgroundColor: 'transparent',
                color: '#94A3B8',
                border: '1px solid #334155',
                padding: '14px',
                borderRadius: '16px',
                fontSize: '14px',
                fontWeight: 'bold',
                cursor: 'pointer',
              }}
            >
              Reset App & Restart
            </button>
          </div>
        </div>
      );
    }
    // Fix: cast 'this' to any to avoid "Property 'props' does not exist on type 'ErrorBoundary'" error
    return (this as any).props.children;
  }
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error("Could not find root element to mount to");
}

const root = ReactDOM.createRoot(rootElement);
root.render(
  <React.StrictMode>
    <ErrorBoundary>
      <App />
    </ErrorBoundary>
  </React.StrictMode>
);
